import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import routingService from '../services/routingService';
import './ProfileIATAPage.css';

const RoutingConfigPage = () => {
    const navigate = useNavigate();
    const { ppId } = useParams();
    const [search, setSearch] = useState('');
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => { loadRoutes(); }, [ppId]); // eslint-disable-line

    const loadRoutes = async () => {
        setLoading(true);
        try {
            const res = await routingService.list(ppId);
            setRoutes(res.data?.routes || []);
        } catch (err) {
            console.error('Failed to load routing', err);
        } finally {
            setLoading(false);
        }
    };

    const filtered = routes.filter(r =>
        !search ||
        (r.code || '').toLowerCase().includes(search.toLowerCase()) ||
        (r.description || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this routing configuration?')) return;
        try {
            await routingService.remove(ppId, id);
            setRoutes(prev => prev.filter(r => r.id !== id));
        } catch (err) {
            console.error('Failed to delete routing', err);
        }
    };

    const RoutingIcon = () => (
        <div className="pi-avatar" style={{ background: '#ede9fe' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="7" cy="7" r="3" stroke="#7c3aed" strokeWidth="1.75"/>
                <circle cx="17" cy="17" r="3" stroke="#7c3aed" strokeWidth="1.75"/>
                <path d="M7 10v4a3 3 0 0 0 3 3h4" stroke="#7c3aed" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
        </div>
    );

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Back to Payment Porter
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">Routing Configuration</h1>
                        <p className="pi-subtitle">Configure Routing Codes to assign to a Reservation</p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    <button className="pi-btn-add" onClick={() => navigate(`/apps/payment-porter/${ppId}/routing/new`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Routing
                    </button>
                </div>
            </div>

            <div className="pi-search-wrap">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pi-search-icon">
                    <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                    <path d="M11 11l3 3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                    type="text"
                    className="pi-search-input"
                    placeholder="Search routing by code or description..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <div className="pi-list-panel">
                {!loading && filtered.length === 0 ? (
                    <div className="pi-empty">
                        <div className="pi-empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <circle cx="9" cy="7" r="3" stroke="#d1d5db" strokeWidth="1.5"/>
                                <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <div className="pi-empty-title">
                            {search ? 'No routing settings match your search' : 'No routing settings configured'}
                        </div>
                        <div className="pi-empty-sub">
                            {search ? 'Try a different search term.' : 'Get started by adding your first routing setting.'}
                        </div>
                    </div>
                ) : (
                    <table className="pi-table">
                        <thead>
                            <tr>
                                <th>Routing Code</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Default Window</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(r => (
                                <tr key={r.id}>
                                    <td>
                                        <div className="pi-profile-cell">
                                            <RoutingIcon />
                                            <span className="pi-id-cell">{r.code}</span>
                                        </div>
                                    </td>
                                    <td>
                                        {r.type ? (
                                            <span className="pi-type-badge">{r.type}</span>
                                        ) : <span className="pi-no-value">—</span>}
                                    </td>
                                    <td>{r.description || <span className="pi-no-value">—</span>}</td>
                                    <td>{r.default_window ? `Window ${r.default_window}` : <span className="pi-no-value">—</span>}</td>
                                    <td>
                                        <span className={r.active ? 'pi-badge-active' : 'pi-badge-inactive'}>
                                            {r.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="pi-actions">
                                            <button
                                                className="pi-btn-edit"
                                                onClick={() => navigate(`/apps/payment-porter/${ppId}/routing/${r.id}/edit`)}
                                                title="Edit"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="pi-btn-delete"
                                                onClick={() => handleDelete(r.id)}
                                                title="Delete"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default RoutingConfigPage;
